/**
 * Phone with one Face ID (docs/e2e.md "Phone with one Face ID"): ONE discoverable-passkey
 * ceremony signs in, endorses this browser's device key and (when the authenticator returns PRF)
 * opens the passkey's v2 PRF escrow so encrypted work is readable at once. No email, no second
 * tap and no approval from another device.
 *
 * The browser first reads through its passkey's PRF escrow recipient (which every rotation
 * re-wraps). A *trusted* browser then joins the directory by itself, signed by its own endorsed
 * device key (docs/e2e.md "Trusted browsers follow rotations"), so later rotations reach it with
 * no passkey tap. A "Just this session" browser stays an escrow reader. Without a PRF result the
 * browser is still signed in and endorsed; content shows the kit / approval fallback.
 */
import {
  approvalBatchItem,
  createApprovalBatchChallenge,
  createDeviceEndorsementChallenge,
  createDeviceEndorsementStatement,
  type DeviceEndorsement,
  DOMAIN,
  derivePrfKek,
  loginBatchItem,
  openDirectoryEscrow,
  openEscrowEpochKeys,
  passkeyPrfSaltV2,
  TrustStore,
  toBase64Url,
  UNLOCK_REQUEST_DOMAIN,
  unwrapAccountKey,
  wipeEpochKeys,
} from '@viko/crypto'
import type { LoginJoin } from '@viko/protocol'
import { type Api, ApiError } from './api.js'
import { clock } from './clock.js'
import { keysApi } from './content-keys-api.js'
import type { BrowserContentSession, KeyContext } from './content-session.js'
import { loadContentTrust, saveContentTrust } from './content-trust.js'
import { ensureDeviceKey, loadDevice, saveEndorsement, sessionLabel } from './device-key.js'
import { rememberedAccount } from './last-account.js'
import type { PasskeyProvider } from './passkeys.js'
import {
  abandonSessionOnly,
  leaveSessionOnlyForTrust,
  rememberSessionDevice,
  startSessionOnly,
} from './session-only.js'
import {
  type ContentUnlockResult,
  prepareUnlock,
  type UnlockPhase,
  viaMachine,
} from './unlock-ceremony.js'

/**
 * - `unlocked`: PRF opened the escrow; encrypted work is readable here now.
 * - `already`: this browser was already a directory recipient.
 * - `no-prf`: the authenticator returned no PRF result (fallback: kit or trusted-device approval).
 * - `no-escrow`: PRF worked, but this passkey has no v2 escrow yet (created at setup or with
 *   *Protect with passkey*); an older v1 escrow needs the separate passkey tap.
 * - `locked`: no content session in this build (mock mode).
 */
export type FaceIdContent = 'unlocked' | 'already' | 'no-prf' | 'no-escrow' | 'locked'

export interface FaceIdDeps {
  api: Pick<Api, 'loginOptions' | 'loginVerify' | 'contentKeys'>
  passkeys: Pick<PasskeyProvider, 'loginJoin'>
  content?: BrowserContentSession
  conditional?: boolean
  origin?: string
  /**
   * "Just this session": the device key is created in memory only (lib/session-only.ts) and the
   * same single ceremony endorses it with `scope: 'session'` (≤ 12 h). Unlock works as usual; the
   * browser stays an escrow reader, never a directory recipient.
   */
  session?: boolean
  /** unlock-via-mac: progress of a machine unlock carried by this sign-in. */
  onPhase?: (p: UnlockPhase) => void
}

export interface FaceIdResult {
  content: FaceIdContent
  /**
   * unlock-via-mac: no PRF, but the sign-in tap also approved this browser's unlock request; a
   * key-holding machine is answering it. Track it (ContentLock.track) instead of showing a lock.
   */
  machine?: Promise<ContentUnlockResult>
}

export async function signInWithFaceId(deps: FaceIdDeps): Promise<FaceIdResult> {
  if (!deps.passkeys.loginJoin) throw new Error('Single-tap sign-in unavailable')
  if (!deps.session) {
    // A trusted sign-in persists its key and pins, even on a page left in memory-only mode.
    leaveSessionOnlyForTrust()
    return signIn(deps)
  }
  startSessionOnly()
  try {
    return await signIn(deps)
  } catch (e) {
    // Cancelled or refused before this browser was endorsed: leave memory-only mode again.
    if (!(await loadDevice())?.endorsement) abandonSessionOnly()
    throw e
  }
}

async function signIn(deps: FaceIdDeps): Promise<FaceIdResult> {
  if (!deps.passkeys.loginJoin) throw new Error('Single-tap sign-in unavailable')
  const dev = await ensureDeviceKey()
  // Built before the ceremony: nothing in it depends on the account or the credential.
  const statement = await createDeviceEndorsementStatement({
    publicKey: dev.publicJwk,
    label: deps.session ? sessionLabel(dev.label) : dev.label,
    now: clock.now(),
    ...(deps.session ? { session: true } : {}),
  })
  // unlock-via-mac: a returning, trusted browser that isn't a key recipient here carries its
  // unlock request in the same ceremony, so a passkey without PRF still unlocks with this one tap.
  const unlock = await prepareSignInUnlock(deps).catch(() => undefined)
  const options = await deps.api.loginOptions()
  const batch = [
    await loginBatchItem(options.challenge),
    approvalBatchItem(DOMAIN.deviceEndorsement, await createDeviceEndorsementChallenge(statement)),
    ...(unlock ? [approvalBatchItem(UNLOCK_REQUEST_DOMAIN, unlock.challenge)] : []),
  ]
  const challenge = toBase64Url(await createApprovalBatchChallenge(batch))
  const { response, prf } = await deps.passkeys.loginJoin(
    { ...options, challenge },
    { prfSalt: await passkeyPrfSaltV2(), ...(deps.conditional ? { conditional: true } : {}) },
  )
  try {
    await deps.api.loginVerify(response, {
      v: 1,
      challenge: options.challenge,
      statement: statement as LoginJoin['statement'],
      ...(unlock ? { unlock: unlock.digest } : {}),
    })
    const endorsement: DeviceEndorsement = {
      statement,
      assertion: {
        credentialId: response.id,
        authenticatorData: response.response.authenticatorData,
        clientDataJSON: response.response.clientDataJSON,
        signature: response.response.signature,
        ...(response.response.userHandle ? { userHandle: response.response.userHandle } : {}),
      },
      batch,
    }
    await saveEndorsement(dev, endorsement)
    if (deps.session) rememberSessionDevice(dev.deviceKeyId)
    if (!deps.content) return { content: 'locked' }
    let content: FaceIdContent = 'no-prf'
    if (prf)
      content = await unlockWithPrf(
        deps.content,
        { credentialId: response.id, output: prf },
        deps.origin,
      )
    if ((content === 'no-prf' || content === 'no-escrow') && unlock) {
      // The same assertion (its batch lists the request digest) goes to the key-holding machine.
      const session = deps.content
      const hadPrf = !!prf
      const machine = (async (): Promise<ContentUnlockResult> => {
        // A different passkey picked another account: this request is not for it.
        if ((await session.accountId().catch(() => undefined)) !== unlock.accountId)
          return { status: 'locked', fallback: content as 'no-prf', prf: hadPrf }
        const r = await viaMachine(
          session,
          { api: deps.api as Api, ...(deps.onPhase ? { onPhase: deps.onPhase } : {}) },
          unlock,
          endorsement.assertion,
          batch,
        )
        return r.status === 'locked'
          ? { ...r, fallback: content as 'no-prf', prf: hadPrf }
          : { ...r, prf: hadPrf }
      })()
      return { content, machine }
    }
    return { content }
  } finally {
    prf?.fill(0)
  }
}

/** A returning trusted browser that is not a directory recipient of its pinned head. */
async function prepareSignInUnlock(deps: FaceIdDeps) {
  if (deps.session || !deps.content) return undefined
  const accountId = await rememberedAccount()
  if (!accountId) return undefined
  const trust = await loadContentTrust(accountId).catch(() => undefined)
  const dev = await loadDevice()
  const head = (await deps.content.vault.state(accountId))?.checkpoint
  if (!trust || !dev || !head) return undefined
  if (head.statement.recipients.some((r) => r.id === dev.deviceKeyId && r.type === 'device'))
    return undefined
  return prepareUnlock(deps.content, accountId, { signIn: true })
}

/**
 * Open this credential's v2 PRF escrow with a PRF output from the sign-in ceremony: pin the
 * escrowed passkeys (authenticated by the PRF KEK), verify the directory chain from genesis and
 * install commitment-checked epoch keys. The hub releases PRF escrow without a hold but records
 * the release, which every signed-in device sees as a recovery alert.
 */
export function unlockWithPrf(
  session: BrowserContentSession,
  prf: { credentialId: string; output: Uint8Array },
  origin = globalThis.location?.origin ?? '',
): Promise<FaceIdContent> {
  return session.exclusive((ctx) => openPrfEscrow(ctx, prf, origin))
}

/**
 * Unlock in place (the locked-content banner): ONE passkey tap that returns the v2 PRF secret,
 * then the same escrow open as the sign-in. No sign-out. A "Just this session" browser stays an
 * escrow reader; a trusted one also joins the directory (no further tap), so it is not left behind
 * again. A reader whose keys fell behind a rotation — a trusted browser from before self-join, or
 * a session browser after a recovery-code or kit rotation — catches up here.
 */
export function unlockWithPasskey(
  session: BrowserContentSession,
  origin = globalThis.location?.origin ?? '',
): Promise<FaceIdContent> {
  return session.exclusive(async (ctx) => {
    if (!ctx.passkeys.prf) return 'no-prf'
    const salt = await passkeyPrfSaltV2()
    let salts: { credentialId: string; salt: Uint8Array }[] = []
    let rpId: string | undefined
    try {
      const trust = await loadContentTrust(ctx.accountId)
      rpId = trust.rp.rpId
      salts = trust
        .credentials()
        .filter((c) => !trust.isCredentialRevoked(c.credentialId))
        .map((c) => ({ credentialId: c.credentialId, salt }))
    } catch {
      // No local pins yet: any of the account's passkeys (discoverable), same v2 salt.
    }
    const prf = await ctx.passkeys.prf(salts, { ...(rpId ? { rpId } : {}), salt })
    if (!prf) return 'no-prf'
    try {
      return await openPrfEscrow(ctx, prf, origin)
    } finally {
      prf.output.fill(0)
    }
  })
}

async function openPrfEscrow(
  ctx: KeyContext,
  prf: { credentialId: string; output: Uint8Array },
  origin: string,
): Promise<FaceIdContent> {
  try {
    const { trust, device } = await ctx.local()
    const directory = await ctx.refresh(trust, device)
    if (
      directory.checkpoint?.statement.recipients.some(
        (r) => r.id === device.deviceKeyId && r.type === 'device',
      )
    )
      return 'already'
  } catch {
    // A new browser: no local pins yet. The escrow provides them below.
  }
  const relay = keysApi(ctx.api)
  let request: Awaited<ReturnType<typeof relay.startRecovery>>
  try {
    request = await relay.startRecovery('prf', prf.credentialId)
  } catch (e) {
    if (e instanceof ApiError && e.code === 'recovery_unavailable') return 'no-escrow'
    throw e
  }
  if (request.status !== 'ready') return 'no-escrow'
  const bundle = await relay.readRecovery(request.id)
  // A v1 escrow uses the per-credential salt: it needs the separate PRF tap.
  if (bundle.escrow.prfSalt !== 2 || bundle.escrow.recipientId !== prf.credentialId)
    return 'no-escrow'
  const kek = await derivePrfKek(prf.output, ctx.accountId, prf.credentialId)
  const opened = await openDirectoryEscrow(bundle.escrow, kek, {
    accountId: ctx.accountId,
    kind: 'prf',
    credentialId: prf.credentialId,
  })
  let trust: TrustStore
  try {
    trust = await loadContentTrust(ctx.accountId)
  } catch {
    trust = new TrustStore({ rp: { rpId: opened.rpId, origins: [origin] }, credentials: [] })
  }
  for (const c of opened.credentials)
    if (
      !trust.isCredentialRevoked(c.credentialId) &&
      !trust.credentials().some((x) => x.credentialId === c.credentialId)
    )
      trust.pinCredential(c)
  const device = await loadDevice()
  if (!device?.endorsement || !(await trust.addEndorsement(device.endorsement)).ok)
    throw new Error('This browser needs a passkey endorsement')
  await saveContentTrust(ctx.accountId, trust)
  await ctx.vault.ensureIdentity(ctx.accountId)
  // The escrowed epoch keys also let this browser check trusted-browser self-join proofs while it
  // verifies the chain (so it does not quarantine the account's other trusted browsers).
  const escrowKey = async (epoch: number) => {
    const w = bundle.wraps.find((x) => x.epoch === epoch)
    if (!w) return undefined
    return unwrapAccountKey(
      w.wrap,
      { version: 1, accountId: ctx.accountId, epoch, recipientId: opened.recipientId },
      opened.privateKey,
      opened.publicKey,
    )
  }
  const directory = await ctx.refresh(trust, device, escrowKey)
  const state = await ctx.vault.state(ctx.accountId)
  if (!directory.checkpoint || !state) throw new Error('Encrypted directory unavailable')
  // The escrow recipient must be live in the verified head (never a relay-only record).
  if (
    !directory.checkpoint.statement.recipients.some(
      (r) => r.id === prf.credentialId && r.type === 'prf',
    )
  )
    throw new Error('This passkey no longer protects your encrypted work')
  const keys = await openEscrowEpochKeys(ctx.accountId, opened, bundle.wraps, state.commitments)
  try {
    await ctx.vault.installRecovered(ctx.accountId, device.deviceKeyId, keys)
  } finally {
    wipeEpochKeys(keys)
  }
  if (!directory.checkpoint.statement.recipients.some((r) => r.id === device.deviceKeyId))
    await ctx.vault.markEscrowReader(ctx.accountId, prf.credentialId)
  await relay.completeRecovery(request.id)
  // A trusted browser joins the directory now, signed by its own endorsed key (no second tap), so
  // later rotations reach it. Best effort: it retries on the next key use.
  await ctx.joinTrusted(trust, device).catch(() => false)
  return 'unlocked'
}
